
export const getAudioBuffer = async (data, onProgress, abortController) => {
    let onConvertingProgress = (percentage) => onProgress?.(percentage);

    // Get the array buffer from audio file or url.
    let arrayBuffer = data instanceof File 
        ? await getArrayBufferFromFile(data, onConvertingProgress, abortController) 
        : await getArrayBufferFromUrl(data, onConvertingProgress, abortController);

    // Create audio context and decode the audio buffer.
    // Unfortunately, the Web Audio API's decodeAudioData method doesn't natively provide progress updates during the decoding process
    let audioContext = new (window.AudioContext || window.webkitAudioContext)();
    let audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    audioContext.close();
    audioContext = null;
    return audioBuffer;
}


async function getArrayBufferFromFile(file, onProgress, abortController) {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader();

        fileReader.onload = () => resolve(fileReader.result);
        fileReader.onerror = () => reject('Cannot read the audio file to create the array buffer.');
        fileReader.onprogress = (event) => onProgress(Math.round((event.loaded / event.total) * 100));
        
        // Handle the cancellation of the audio file reading.
        if (abortController.signal) {
            if (abortController.signal.aborted)
                return reject('The audio file reading has been aborted.');
            
            const abortHandler = () => {
                fileReader.abort();
                console.log("abortHandler");
                reject('The audio file reading has been aborted.');
            };
            
            abortController.signal.addEventListener('abort', abortHandler);
            fileReader.onloadend = () => abortController.signal.removeEventListener('abort', abortHandler);
        }
        
        // Start reading the audio file.
        fileReader.readAsArrayBuffer(file);
    });
}


async function getArrayBufferFromUrl(url, onProgress, abortController) {
    try {
        // Check if already aborted before starting the fetch
        if (abortController.signal && abortController.signal.aborted)
            throw new Error('The audio URL fetch has been aborted.');

        // Pass the abortSignal to fetch to allow cancellation
        const response = await fetch(url, { signal: abortController.signal });
        if (!response.ok)
            throw new Error(`Cannot fetch the audio url to create the array buffer. HTTP error: ${response.status}`);

        const reader = response.body.getReader();
        const contentLength = +response.headers.get('Content-Length') || 0;
        let receivedLength = 0;
        let chunks = [];

        while (true) {
            // Check if aborted before each read operation
            if (abortController.signal && abortController.signal.aborted)
                throw new Error('The audio URL fetch has been aborted.');
            
            const { done, value } = await reader.read();
            if (done)
                break;

            chunks.push(value);
            receivedLength += value.length;
            onProgress(Math.round((receivedLength / contentLength) * 100));
        }

        // Check if aborted before processing chunks
        if (abortController.signal && abortController.signal.aborted)
            throw new Error('The audio URL fetch has been aborted.');

        // Concatenate chunks into a single Uint8Array
        let arrayBuffer = new Uint8Array(receivedLength);
        let position = 0;

        for (const chunk of chunks) {
            arrayBuffer.set(chunk, position);
            position += chunk.length;
        }

        // Return the array buffer with audio file data.
        return arrayBuffer.buffer;
    } catch (error) {
        throw new Error(`Cannot fetch the audio url to create the array buffer. ${error.message}`);
    }
}